import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './VendorAssessment.css';
import VendorAssessmentForm from './components/VendorAssessmentForm';

const PAGE_TITLE = 'Vendor Risk Snapshot - Trust Powered AI';
const PAGE_DESCRIPTION =
  'A defensible AI vendor risk assessment against 43 master requirements drawn from the EU AI Act, GDPR, SOC 2 Type II, and ISO 42001. Delivered in two formats - an interactive report and a DOCX report - within 24 hours.';

function VendorAssessment() {
  // Per-route <head> management for a CRA SPA (no SSR / react-helmet).
  // Sets title + description on mount and restores on unmount. Googlebot
  // renders this; non-JS social scrapers will not (documented limitation).
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute('content') : null;
    if (metaDesc) metaDesc.setAttribute('content', PAGE_DESCRIPTION);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="va-page">
      {/* Standalone header (matches the PrivacyPolicy secondary-page pattern) */}
      <header className="va-header">
        <div className="va-header-container">
          <Link to="/" className="va-back-link">← Back to Home</Link>
        </div>
      </header>

      {/* Section 1: Hero - CTA kept high so it's visible without scrolling */}
      <section className="va-hero-section">
        <div className="container">
          <div className="va-hero va-two-col">
            <div className="va-hero-left">
              <span className="section-tag">🛡️ Vendor Assessment</span>
              <h1 className="va-hero-title">
                Know what your AI vendor will fail on before procurement does.
              </h1>
              <p className="va-hero-subtitle">
                A defensible AI vendor risk assessment, delivered in two formats - an interactive
                report and a DOCX report - ready to forward to Legal, Security, or your customer's
                procurement team.
              </p>
              <div className="va-hero-actions">
                <a href="#snapshot" className="va-cta-button va-hero-cta">Request your snapshot →</a>
                <div className="va-trust-badges">
                  <span className="va-trust-badge"><span className="va-trust-icon">⏱️</span> 24-hour turnaround</span>
                  <span className="va-trust-badge"><span className="va-trust-icon">✅</span> No credit card</span>
                  <span className="va-trust-badge"><span className="va-trust-icon">🎁</span> One vendor, on us</span>
                </div>
              </div>
            </div>

            {/* Right column - sample dashboard preview card */}
            <div className="va-hero-right">
              <div className="va-sample-card" aria-label="Sample assessment output">
                <div className="va-sample-top">
                  <div className="va-sample-vendor">
                    <span className="va-sample-eyebrow">Sample · AI vendor · illustrative</span>
                    <span className="va-sample-vendor-name">FlowScribe AI</span>
                    <span className="va-sample-caption">Illustrative preview of the deliverable</span>
                  </div>
                  <span className="va-sample-label">Sample</span>
                </div>

                <div className="va-verdict-band amber">
                  <div className="va-verdict-band-left">
                    <span className="va-verdict-dot" aria-hidden="true"></span>
                    <div>
                      <span className="va-verdict-label">Verdict</span>
                      <span className="va-verdict-value">Recommended with contract additions</span>
                    </div>
                  </div>
                  <div className="va-verdict-score">
                    <span className="va-score-number">-</span>
                    <span className="va-score-caption">FinalScore / 100</span>
                  </div>
                </div>

                <div className="va-scope-strip">
                  <span><strong>Scope:</strong> X of 43 requirements applied</span>
                  <span><strong>Evidence:</strong> verified vs. inferred</span>
                </div>

                <div className="va-status-counts">
                  <span className="va-count compliant">Compliant</span>
                  <span className="va-count partial">Partial</span>
                  <span className="va-count noncompliant">Non-Compliant</span>
                  <span className="va-count pending">Pending</span>
                </div>

                <div className="va-concerns">
                  <div className="va-concerns-header">Top concerns · surfaced from the findings table</div>
                  <div className="va-concern-row">
                    <span className="va-concern-num">01</span>
                    <span className="va-concern-text">Each finding maps to a specific requirement, with evidence</span>
                    <span className="va-status-pill noncompliant">Non-Compliant</span>
                  </div>
                  <div className="va-concern-row">
                    <span className="va-concern-num">02</span>
                    <span className="va-concern-text">Partial findings note exactly what's missing</span>
                    <span className="va-status-pill partial">Partial</span>
                  </div>
                  <div className="va-concern-row">
                    <span className="va-concern-num">03</span>
                    <span className="va-concern-text">Thin or auth-walled evidence becomes a vendor follow-up</span>
                    <span className="va-status-pill pending">Pending</span>
                  </div>
                </div>

                <div className="va-tab-dots">
                  <span className="va-tab-dot">● Executive Summary</span>
                  <span className="va-tab-dot">● Scope</span>
                  <span className="va-tab-dot">● Findings</span>
                  <span className="va-tab-dot">● Concerns</span>
                  <span className="va-tab-dot">● Follow-Up Questions</span>
                  <span className="va-tab-dot">● Methodology</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Frameworks (trimmed, lean) */}
      <section className="section va-frameworks-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">📦 Powered By</span>
            <h2 className="section-title">Built on the frameworks your customers audit against.</h2>
            <p className="va-section-subhead">
              <strong>43 master requirements</strong> drawn from four frameworks. Only the requirements
              that apply to your use case activate - the report's scope note tells you exactly which.
            </p>
          </div>
          <div className="framework-badges va-frameworks-row">
            <span className="framework-badge euai">EU AI Act</span>
            <span className="framework-badge gdpr">GDPR</span>
            <span className="framework-badge soc2">SOC 2 Type II</span>
            <span className="framework-badge iso">ISO 42001</span>
          </div>
        </div>
      </section>


      {/* Section 3: The Form */}
      <section id="snapshot" className="section va-form-section">
        <div className="container">
          <div className="va-two-col va-form-layout">
            <div className="va-form-intro">
              <span className="section-tag">📥 Request</span>
              <h2 className="section-title">One vendor. On us.</h2>
              <p className="va-form-para">
                Most CTOs use the first snapshot on the AI vendor they're already three weeks deep
                into evaluating - the one Legal hasn't cleared and Security is asking questions about.
              </p>
              <p className="va-form-para">
                Tell us which one. We'll run the scoping interview, do the research, and deliver the
                assessment by end of day tomorrow.
              </p>
              <div className="va-callout">
                <span className="va-callout-icon">💡</span>
                <p>
                  <strong>What happens next:</strong> within 24 hours you receive your assessment in
                  two formats - an interactive report (HTML) and a DOCX report you can forward to
                  Legal, Security, or your customer.
                </p>
              </div>
            </div>
            <div className="va-form-wrap">
              <VendorAssessmentForm />
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: How It Works */}
      <section className="section va-how-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">⚙️ How It Works</span>
            <h2 className="section-title">Three steps. Twenty-four hours.</h2>
            <p className="va-section-subhead">
              No discovery call to start. A short scoping interview determines which requirements apply
              to your use case - then we do the research and produce the assessment.
            </p>
          </div>
          <div className="pillars-grid">
            <div className="pillar-card pillar-1">
              <div className="pillar-number">1</div>
              <div className="pillar-icon-large">📝</div>
              <h3>You submit a vendor</h3>
              <p>
                Name the AI vendor you're evaluating, what data it touches, and what's prompting the
                review. Takes about 90 seconds.
              </p>
              <div className="pillar-features">
                <span className="feature-tag">~ 90 seconds</span>
              </div>
            </div>
            <div className="pillar-card pillar-2">
              <div className="pillar-number">2</div>
              <div className="pillar-icon-large">🎯</div>
              <h3>Scoping activates the right requirements</h3>
              <p>
                A short structured interview - data types, regions, AI behaviour - determines which
                requirements apply. Conservative defaults where you're unsure, flagged in the report.
              </p>
              <div className="pillar-features">
                <span className="feature-tag">Scoped to your use case</span>
              </div>
            </div>
            <div className="pillar-card pillar-3">
              <div className="pillar-number">3</div>
              <div className="pillar-icon-large">📊</div>
              <h3>Two reports in your inbox</h3>
              <p>
                An interactive report with filterable findings, evidence trail, and vendor follow-up
                questions - plus a DOCX report for sharing with Legal, Security, or your customer.
              </p>
              <div className="pillar-features">
                <span className="feature-tag">Within 24 hours</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: The Verdict */}
      <section className="section va-verdict-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">📋 The Verdict</span>
            <h2 className="section-title">The decision you walk away with.</h2>
            <p className="va-section-subhead">
              Every assessment lands on one of four clear recommendation bands - or an honest "not
              enough evidence" - plus a FinalScore (0-100) and an Evidence Completeness percentage so
              you know how much of the picture is verified versus inferred.
            </p>
          </div>
          <div className="va-verdict-grid">
            <div className="va-verdict-card green">
              <span className="va-verdict-card-title">Recommended</span>
              <p>Sign as drafted. Standard ongoing monitoring.</p>
            </div>
            <div className="va-verdict-card amber">
              <span className="va-verdict-card-title">Recommended with contract additions</span>
              <p>Sign once specific contract clauses are added. Clauses provided.</p>
            </div>
            <div className="va-verdict-card red">
              <span className="va-verdict-card-title">Recommended with significant remediation</span>
              <p>Material gaps. Sign only after the vendor closes them - remediation list included.</p>
            </div>
            <div className="va-verdict-card darkred">
              <span className="va-verdict-card-title">Not recommended without significant remediation</span>
              <p>Don't sign as-is. Specific blockers prevent compliant use.</p>
            </div>
            <div className="va-verdict-card gray full-width">
              <span className="va-verdict-card-title">Insufficient Evidence - Preliminary</span>
              <p>
                When evidence completeness falls below 50%, we don't force a band - we tell you
                exactly what additional documentation would be needed to issue one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: What's inside the deliverable (compact) */}
      <section className="section va-inside-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">📦 The Deliverable</span>
            <h2 className="section-title">How that verdict arrives: two reports.</h2>
            <p className="va-section-subhead">
              The verdict doesn't come as a one-line email. You get an interactive report (HTML) and a
              DOCX report - same six tabs in both - so you can explore the reasoning in-session, then
              forward the DOCX to Legal or Security without rewriting.
            </p>
          </div>
          <div className="va-inside-tabs">
            <div className="va-inside-tab">
              <span className="va-inside-tab-name">Executive Summary</span>
              <p>The verdict, FinalScore, and rationale - with the decision-relevant strengths and concerns.</p>
            </div>
            <div className="va-inside-tab">
              <span className="va-inside-tab-name">Scope</span>
              <p>Exactly which requirements were in and out of scope, and why. No silent gaps.</p>
            </div>
            <div className="va-inside-tab">
              <span className="va-inside-tab-name">Findings</span>
              <p>Requirement-by-requirement, filterable by status and severity, each with evidence.</p>
            </div>
            <div className="va-inside-tab">
              <span className="va-inside-tab-name">Concerns</span>
              <p>AI-specific risks surfaced beyond the requirement set.</p>
            </div>
            <div className="va-inside-tab">
              <span className="va-inside-tab-name">Follow-Up Questions</span>
              <p>Vendor-ready questions where evidence is thin - each tied to what it resolves.</p>
            </div>
            <div className="va-inside-tab">
              <span className="va-inside-tab-name">Methodology</span>
              <p>The full audit trail and the scoring math - defensible against later review.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7: FAQ */}
      <section className="section va-faq-section">
        <div className="container va-faq-container">
          <div className="section-header">
            <span className="section-tag">❓ Honest FAQ</span>
            <h2 className="section-title">Questions you'd reasonably ask.</h2>
          </div>
          <div className="va-faq">
            <details className="va-faq-item">
              <summary>Is the snapshot really free?</summary>
              <div className="va-faq-answer">
                Yes. One vendor per company, no card, no sales call required to receive it. Both
                reports - the interactive HTML and the DOCX - land in your inbox within 24 hours. If
                you want to walk through it with us, or get a second vendor assessed, that's where the
                call or the $99 skill comes in - both optional.
              </div>
            </details>
            <details className="va-faq-item">
              <summary>How can you assess a vendor without their cooperation?</summary>
              <div className="va-faq-answer">
                Most of what matters is already public or semi-public: trust pages, attestation
                letters, sub-processor lists, model cards, terms of service, DPAs available on
                request, EU AI Act risk classifications, retention policies. Our framework captures
                verbatim quotes from those sources and maps them to the 43 master requirements. Where
                evidence is auth-walled or thin, the report says so - and we never bypass auth walls
                or use leaked copies.
              </div>
            </details>
            <details className="va-faq-item">
              <summary>What if there genuinely isn't enough public evidence?</summary>
              <div className="va-faq-answer">
                When Evidence Completeness falls below 50%, we don't force a verdict band. The report
                returns "Insufficient Evidence - Preliminary" and gives you a specific list of
                documents and questions to request from the vendor. You get a defensible reason to
                push for more, not a confident guess.
              </div>
            </details>
            <details className="va-faq-item">
              <summary>What does "43 master requirements" actually mean?</summary>
              <div className="va-faq-answer">
                43 requirements consolidated from the EU AI Act, GDPR, SOC 2 Type II, and ISO 42001.
                Only those that apply to your specific use case activate. The report's scope note
                ("X of 43 applied") tells you exactly what was in and out of scope, with reasoning.
              </div>
            </details>
            <details className="va-faq-item">
              <summary>How is this different from a vendor security questionnaire?</summary>
              <div className="va-faq-answer">
                A questionnaire assumes the vendor will answer honestly and completely. Our assessment
                is the inverse - it's what you should ask, in what order, what counts as a real answer,
                and where to push back. It also covers AI-specific risk that standard questionnaires
                don't (model behaviour, training-data provenance, prompt-injection exposure, retention
                of inference data, sub-processor model providers).
              </div>
            </details>
            <details className="va-faq-item">
              <summary>How does this fit Trust Powered AI's broader framework?</summary>
              <div className="va-faq-answer">
                The Vendor Risk Snapshot is the Control pillar of our Three-Pillar Framework applied
                to your AI vendor stack. It's also the fastest way to experience how the framework
                operates - see the <Link to="/" className="va-inline-link">framework on our home page</Link>.
              </div>
            </details>
            <details className="va-faq-item">
              <summary>What if I want a real engagement, not a one-off?</summary>
              <div className="va-faq-answer">
                The walkthrough is where we scope that. Typical follow-ons include a pilot covering
                3-5 vendors, a Unified AI Compliance Playbook engagement (Monitoring + Control +
                Education across your entire AI footprint), or ongoing monthly vendor review as a
                retainer. None of that is required to get value from the free snapshot.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>&copy; 2026 TrustPoweredAI. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default VendorAssessment;
